import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Users, Phone } from "lucide-react";
import SEOHead from "@/components/SEOHead";

const IMAGES = {
  volleyball: "/volleyball-lineup.jpg",
  basketball: "/basketball-team.jpg",
  gymnastics: "/gymnastics-group.jpg",
};

const branches = [
  {
    name: "Voleybol",
    slug: "voleybol",
    image: IMAGES.volleyball,
    federation: "TVF",
    federationLogo: "/tvf-logo.png",
    desc: "Voleybol branşımızda, temel teknik becerilerin yanında takım oyunu, strateji ve müsabaka deneyimi kazandırmaktayız. Profesyonel antrenörlerimiz eşliğinde her yaş grubuna uygun eğitim programları sunulmaktadır.",
    details: [
      { icon: Users, label: "Yaş Grubu", value: "7-14 Yaş" },
    ],
    highlights: [
      "Temel voleybol teknikleri (servis, pas, smaç)",
      "Takım oyunu ve strateji geliştirme",
      "Lisanslı sporcu olma imkânı",
      "Federasyon liglerinde mücadele",
      "Fiziksel gelişim ve kondisyon çalışması",
    ],
  },
  {
    name: "Basketbol",
    slug: "basketbol",
    image: IMAGES.basketball,
    federation: "TBF",
    federationLogo: "/tbf-logo.png",
    desc: "Basketbol branşımız, bireysel yeteneklerin geliştirilmesinin yanı sıra, takım içi uyum ve liderlik becerilerinin kazandırılmasına odaklanmaktadır. Modern antrenman metodlarıyla çocuklarımızı geleceğin basketbolcuları olarak yetiştiriyoruz.",
    details: [
      { icon: Users, label: "Yaş Grubu", value: "6-14 Yaş" },
    ],
    highlights: [
      "Temel basketbol teknikleri (dribling, şut, pas)",
      "Bireysel ve takım taktikleri",
      "Lisanslı sporcu olma imkânı",
      "Federasyon liglerinde mücadele",
      "Fiziksel ve mental gelişim programları",
    ],
  },
  {
    name: "Cimnastik",
    slug: "cimnastik",
    image: IMAGES.gymnastics,
    federation: "TGF",
    federationLogo: "/tgf-logo.png",
    desc: "Cimnastik branşımızda çocukların motor becerilerini, esnekliklerini ve koordinasyonlarını geliştirmeye yönelik kapsamlı eğitim programları uygulanmaktadır. Güvenlik her zaman öncelikli önceliğimizdir.",
    details: [
      { icon: Users, label: "Yaş Grubu", value: "5-12 Yaş" },
    ],
    highlights: [
      "Temel cimnastik hareketleri ve teknikleri",
      "Esneklik ve koordinasyon çalışması",
      "Lisanslı sporcu olma imkânı",
      "Federasyon şampiyonalarında mücadele",
      "Güvenli ve kontrollü ortamda çalışma",
    ],
  },
];

export default function BranchesPage() {
  return (
    <div data-testid="branches-page" className="pt-20">
      <SEOHead
        title={"Spor Branşları - Voleybol, Basketbol, Cimnastik Kursu Ataşehir, Kadıköy, Maltepe"}
        description={"İRÜ Spor Kulübü spor branşları. Ataşehir, Kadıköy, Maltepe bölgesinde voleybol, basketbol ve cimnastik kursları. TVF, TBF, TGF lisanslı eğitim. Anadolu yakası spor okulu."}
        keywords={"spor branşları İstanbul, voleybol basketbol cimnastik kursu, Kadıköy spor kursları, Ataşehir spor eğitimi, Maltepe spor okulu, Anadolu yakası spor branşları, çocuk spor eğitimi, lisanslı spor kulübü"}
        canonical="/branslarimiz"
      />
      {/* Hero */}
      <section className="bg-[#1A1A1A] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-accent text-sm uppercase tracking-widest text-gold">{"Spor Branşları"}</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white mt-2">
            {"Branşlarımız"}
          </h1>
          <p className="mt-4 text-base text-white/60 max-w-lg">
            {"Üç farklı branşta profesyonel eğitim imkânı sunuyoruz."}
          </p>
        </div>
      </section>

      {/* Branches */}
      {branches.map((branch, index) => (
        <section
          key={branch.name}
          data-testid={`branch-${branch.name.toLowerCase()}`}
          className={`py-24 lg:py-32 ${index % 2 === 0 ? "bg-white" : "bg-surface"}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${index % 2 === 1 ? "lg:flex-row-reverse" : ""}`}>
              <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                <Link to={`/branslarimiz/${branch.slug}`} className="block relative overflow-hidden group">
                  <img
                    src={branch.image}
                    alt={branch.name}
                    className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 bg-burgundy px-4 py-2">
                    <span className="font-heading text-sm font-bold uppercase tracking-wider text-white">
                      {branch.name}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <img src={branch.federationLogo} alt={`${branch.federation} Logo`} className="w-12 h-12 object-contain bg-white/90 backdrop-blur-sm rounded-full p-1" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                    <span className="inline-flex items-center gap-1 font-heading text-sm uppercase tracking-wider text-white">
                      {"Detaylı Bilgi"} <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </div>

              <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xs font-accent uppercase tracking-widest text-burgundy bg-burgundy/5 px-3 py-1">{`${branch.federation} Lisanslı Kulüp`}</span>
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A]">
                  {branch.name}
                </h2>
                <p className="mt-4 text-gray-600 leading-relaxed">{branch.desc}</p>

                {/* Details */}
                <div className="flex flex-wrap gap-6 mt-8">
                  {branch.details.map((detail) => (
                    <div key={detail.label} className="flex items-center gap-2">
                      <detail.icon className="w-4 h-4 text-burgundy" strokeWidth={1.5} />
                      <div>
                        <p className="text-xs font-accent uppercase tracking-wider text-gray-400">{detail.label}</p>
                        <p className="text-sm font-semibold text-[#1A1A1A]">{detail.value}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Highlights */}
                <ul className="mt-8 space-y-2">
                  {branch.highlights.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="w-1.5 h-1.5 bg-burgundy rounded-full mt-1.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>

                <div className="flex flex-wrap gap-4 mt-8">
                  <Link to={`/branslarimiz/${branch.slug}`}>
                    <Button
                      data-testid={`branch-detail-${branch.name.toLowerCase()}`}
                      className="bg-burgundy hover:bg-burgundy-700 text-white font-heading uppercase tracking-wider text-sm px-8 py-5"
                    >
                      {"Detaylı Bilgi"} <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/iletisim">
                    <Button
                      data-testid={`branch-info-${branch.name.toLowerCase()}`}
                      variant="outline"
                      className="border-burgundy/30 text-burgundy hover:bg-burgundy/5 font-heading uppercase tracking-wider text-sm px-8 py-5"
                    >
                      <Phone className="w-4 h-4 mr-2" /> Bilgi Al
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}
